import { Component, Input, Output, EventEmitter, ElementRef, ViewChild, ChangeDetectionStrategy, forwardRef, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'smart-input',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule],
  templateUrl: './smart-input.component.html',
  styleUrls: ['./smart-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => SmartInputComponent),
    multi: true
  }]
})
export class SmartInputComponent implements ControlValueAccessor {
  
  // --- INPUTS DE BASE ---
  @Input() placeholder = '';
  @Input() disabled = false;
  @Input() type: 'text' | 'password' | 'email' | 'number' | 'tel' | 'url' = 'text';
  @Input() readonly = false;
  @Input() maxlength: number | null = null;
  @Input() minlength: number | null = null;
  @Input() autocomplete = 'off';
  @Input() width: string = '200px';  // Largeur du wrapper (ex: '100%', '250px', 'auto')
  
  // --- ICÔNE ET ACTION ---
  @Input() icon: string | null = null;  // Nom mat-icon OU svg path selon iconType
  @Input() iconType: 'material' | 'svg' = 'material';  // Type d'icône (material par défaut)
  @Input() iconPosition: 'left' | 'right' = 'right';
  @Input() iconClass: string = '';  // Classes CSS additionnelles pour l'icône (ex: 'text-primary')
  @Input() iconAction: (() => void) | null = null;
  @Input() iconHref: string | null = null;    // Cible du lien quand l'icône doit être un vrai <a> (null = lien inerte)
  @Input() iconTarget: string | null = null;  // '_blank' pour ouvrir dans un nouvel onglet

  // L'icône reste un <button> par défaut. Dès qu'on lui passe une cible elle devient un vrai <a> :
  // clic milieu, ctrl+clic et « ouvrir dans un nouvel onglet » doivent marcher comme sur n'importe
  // quel lien. iconHref peut retomber à null (saisie vide) sans repasser en <button> : le lien est
  // alors sans href, donc inerte, plutôt que de faire danser le DOM à chaque frappe.
  get iconIsLink(): boolean { return this.iconHref !== null || this.iconTarget !== null; }

  @Input() set initialValue(val: string) {  if (val) this.value.set(val);}
  
  // --- CALLBACKS PERSONNALISÉS ---
  @Input() onEnterFn: ((value: string) => void) | null = null;
  @Input() onBlurFn: ((value: string) => void) | null = null;
  
  // --- OUTPUTS ---
  @Output() valueChange = new EventEmitter<string>();
  @Output() enterPressed = new EventEmitter<string>();
  @Output() blurred = new EventEmitter<string>();
  @Output() iconClicked = new EventEmitter<string>();
  
  @ViewChild('inputElement') inputElement!: ElementRef<HTMLInputElement>;
  
  readonly value = signal<string>('');
  readonly isFocused = signal<boolean>(false);
  
  private onChange: (value: string) => void = () => {};
  public onTouched: () => void = () => {};

  // --- GESTION DES ÉVÉNEMENTS ---
  
  onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const newValue = target.value;
    this.value.set(newValue);
    this.onChange(newValue);
    this.valueChange.emit(newValue);
  }
  
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && this.isFocused()) {
      event.preventDefault();
      const currentValue = this.value();
      this.enterPressed.emit(currentValue);
      if (this.onEnterFn) {
        this.onEnterFn(currentValue);
      }
    }
  }
  
  onFocus(): void {
    this.isFocused.set(true);
  }
  
  onBlur(): void {
    this.isFocused.set(false);
    this.onTouched();
    const currentValue = this.value();
    this.blurred.emit(currentValue);
    if (this.onBlurFn) {
      this.onBlurFn(currentValue);
    }
  }
  
  onIconClick(event: MouseEvent): void {
    event.stopPropagation();
    if (this.disabled) { event.preventDefault(); return; }  // <a> n'a pas d'attribut disabled : on bloque l'action par défaut
    const currentValue = this.value();
    this.iconClicked.emit(currentValue);
    if (this.iconAction) {
      this.iconAction();
    }
  }
  
  // --- MÉTHODES UTILITAIRES ---
  
  focus(): void {
    this.inputElement?.nativeElement?.focus();
  }
  
  blur(): void {
    this.inputElement?.nativeElement?.blur();
  }
  
  clear(): void {
    this.value.set('');
    this.onChange('');
    this.valueChange.emit('');
  }

  // --- CONTROL VALUE ACCESSOR ---
  
  writeValue(value: string): void {
    this.value.set(value ?? '');
  }
  
  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }
  
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}